import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

import { firstValueFrom } from 'rxjs';

import { MenuProduct } from '../../../models/menu-product.model';
import { MenuProductOption } from '../../../models/menu-product-option.model';
import { MenuProductOptionService } from '../../../services/menu-product-option/menu-product-option.service';

type DialogData = {
  product: MenuProduct;
};

@Component({
  selector: 'ui-menu-product-options-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatButtonModule, MatIconModule, MatSnackBarModule],
  templateUrl: './menu-product-options-dialog.component.html',
  styleUrls: ['./menu-product-options-dialog.component.css']
})
export class MenuProductOptionsDialogComponent implements OnInit {
  loading = false;
  saving = false;

  productUuid: string = '';
  productName: string = '';

  options: MenuProductOption[] = [];
  originalOptions: MenuProductOption[] = [];

  newOptionName: string = '';
  newOptionPriceDelta: number = 0;

  constructor(
    private readonly ref: MatDialogRef<MenuProductOptionsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private readonly optionsSvc: MenuProductOptionService,
    private readonly snack: MatSnackBar,
  ) {
    const p: any = data?.product as any;
    this.productUuid = String(p?.uuid || p?.id || '');
    this.productName = String(p?.name || '');
  }

  ngOnInit(): void {
    this.load();
  }

  close(): void {
    this.ref.close();
  }

  async load(): Promise<void> {
    if (!this.productUuid) {
      this.options = [];
      this.originalOptions = [];
      return;
    }

    this.loading = true;
    try {
      const opts = await firstValueFrom(this.optionsSvc.getOptions(this.productUuid));
      this.options = (Array.isArray(opts) ? opts : []).map(opt => ({
        ...opt,
        uuid: String(opt?.uuid || ''),
        name: String(opt?.name || ''),
        priceDelta: Number(opt?.priceDelta || 0),
        enabled: opt?.enabled !== false,
        sortOrder: opt?.sortOrder ?? 0,
      }));
      this.originalOptions = structuredClone(this.options);
    } catch (err) {
      console.error('Load options error', err);
      this.options = [];
      this.originalOptions = [];
      this.snack.open('No se pudieron cargar las opciones', 'Cerrar', { duration: 2500 });
    } finally {
      this.loading = false;
    }
  }

  addOption(): void {
    const name = (this.newOptionName || '').toString().trim();
    if (!name) return;
    this.options.push({
      name,
      priceDelta: Number(this.newOptionPriceDelta || 0),
      enabled: true,
      sortOrder: this.options.length,
    });
    this.newOptionName = '';
    this.newOptionPriceDelta = 0;
  }

  removeOption(index: number): void {
    if (index >= 0 && index < this.options.length) {
      this.options.splice(index, 1);
    }
  }

  reset(): void {
    this.options = structuredClone(this.originalOptions);
  }

  hasChanges(): boolean {
    return JSON.stringify(this.options) !== JSON.stringify(this.originalOptions);
  }

  async save(): Promise<void> {
    if (!this.productUuid) return;

    this.saving = true;
    try {
      // Optimized CRUD: compare original vs current to detect changes
      const originalMap = new Map(this.originalOptions.map(opt => [opt.uuid || '', opt]));
      const currentMap = new Map(this.options.map((opt, idx) => [opt.uuid || `new- ${idx} `, opt]));

      // 1. Delete removed options
      for (const [uuid] of originalMap) {
        if (uuid && !currentMap.has(uuid)) {
          await firstValueFrom(this.optionsSvc.deleteOption(this.productUuid, uuid));
        }
      }

      // 2. Update modified options and create new ones
      for (const [key, current] of currentMap) {
        if (key.startsWith('new-')) {
          // Create new option
          await firstValueFrom(this.optionsSvc.createOption(this.productUuid, {
            name: current.name,
            priceDelta: current.priceDelta,
            enabled: current.enabled,
            sortOrder: current.sortOrder,
          }));
        } else if (current.uuid) {
          // Update existing option if changed
          const original = originalMap.get(current.uuid);
          if (original && (
            original.name !== current.name ||
            original.priceDelta !== current.priceDelta ||
            original.enabled !== current.enabled ||
            original.sortOrder !== current.sortOrder
          )) {
            await firstValueFrom(this.optionsSvc.updateOption(this.productUuid, current.uuid, {
              name: current.name,
              priceDelta: current.priceDelta,
              enabled: current.enabled,
              sortOrder: current.sortOrder,
            }));
          }
        }
      }

      this.snack.open('Opciones guardadas', 'Cerrar', { duration: 2000 });
      this.ref.close(true);
    } catch (err) {
      console.error('Save options error', err);
      this.snack.open('No se pudieron guardar las opciones', 'Cerrar', { duration: 2500 });
    } finally {
      this.saving = false;
    }
  }
}
